// Design System Icon Component
// Wrapper for consistent icon styling

import React from 'react';
import { SvgIcon, styled } from '@mui/material';
import PropTypes from 'prop-types';
import { colorTokens } from '../../tokens';

// Styled icon wrapper
const StyledIcon = styled('span')(({ theme, size = 'medium', color }) => {
  const sizeMap = {
    small: 20,
    medium: 24,
    large: 32,
  };
  
  const iconSize = sizeMap[size] || size;
  
  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: iconSize,
    height: iconSize,
    color: color || 'inherit',
    
    '& svg': {
      width: iconSize,
      height: iconSize,
      fontSize: iconSize,
    },
  };
});

/**
 * Icon - Consistent icon wrapper
 * 
 * Features:
 * - Size presets (small, medium, large)
 * - Color support
 * - Works with any icon component
 * 
 * @example
 * // Basic usage
 * <Icon>
 *   <HomeIcon />
 * </Icon>
 * 
 * // With size and color
 * <Icon size="large" color="primary.main">
 *   <SettingsIcon />
 * </Icon>
 * 
 * // Custom size
 * <Icon size={48}>
 *   <BusinessIcon />
 * </Icon>
 */
const Icon = ({
  children,
  size = 'medium',
  color,
  component = 'span',
  ...props
}) => {
  // If children is a Material-UI icon component, clone it with size
  if (React.isValidElement(children) && children.type && children.type.muiName === 'SvgIcon') {
    const iconSize = typeof size === 'string' ? size : undefined;
    return React.cloneElement(children, {
      fontSize: iconSize,
      sx: {
        color,
        ...(typeof size === 'number' && {
          width: size,
          height: size,
          fontSize: size,
        }),
        ...children.props.sx,
      },
    });
  }
  
  // Otherwise wrap in styled component
  return (
    <StyledIcon
      as={component}
      size={size}
      color={color}
      {...props}
    >
      {children}
    </StyledIcon>
  );
};

Icon.propTypes = {
  children: PropTypes.node.isRequired,
  size: PropTypes.oneOfType([
    PropTypes.oneOf(['small', 'medium', 'large']),
    PropTypes.number,
  ]),
  color: PropTypes.string,
  component: PropTypes.elementType,
};

export default Icon;